import { useQuery } from '@apollo/client';
import cx from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';

import Backdrop from '@mui/material/Backdrop';
import Badge from '@mui/material/Badge';
import Collapse from '@mui/material/Collapse';
import Modal from '@mui/material/Modal';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';

import LowBattery from 'assets/icons/LowBattery.svg';
import LowSignalStrength from 'assets/icons/LowSignalStrength.svg';

import { GET_ALL_FILTER_TAGS_V2 } from 'GraphQL/queries/mapService';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
	ExclamationIcon,
	DoubleExclamationIcon,
	TripleExclamationIcon,
} from 'modules/Telematics/components/LeftPanel/components/CustomIcons';
import {
	ILiveMachineData,
	IMachineIcon,
	Layer,
	MarkerIconType,
} from 'interfaces/maps.interface';
import { Coordinates, PlaceTag } from 'interfaces/vector.interface';

import {
	ITagFilters,
	setActiveAlertFilters,
	setActiveTagFilters,
	setSelectedLiveMachine,
} from 'reducers/telematics.reducer';

import useApolloClientAndMsalAuth from 'customHooks/useApolloClientAndMsalAuth';
import { useSearchParams } from 'customHooks/useSearchParams';

import { smoothlyPanMapsTo } from 'utils/customPanTo';
import extractValuesForPlaceTag from 'utils/extractValuesForPlaceTag';
import getDuration from 'utils/getDuration';

import { redirect } from 'actions/navigation';
// import { ALL_LAYERS } from 'modules/AwdMapView/constants';
import { mapOptions } from 'modules/Telematics/components/GoogleMapView';
import { ALERT_ICONS } from 'modules/Telematics/constants';
import { Box } from '@mui/material';
import Filters from './components/Filters';

import styles from './index.module.css';

interface IAlertSummary extends IMachineIcon {
	count: number;
}

const defaultTagFilters: ITagFilters = {
	combiningOperator: 'AND',
	conditions: [
		{
			baseCondition: {
				logicalOperator: 'EQUAL',
				tag: {
					TagName: 'IOT_DEVICE',
					TagPropertyKey: 'TagProperty_STATE',
					value: {
						valueType: 'VALUE_TYPE_STRING',
						stringValues: [],
					},
				},
			},
		},
		{
			baseCondition: {
				logicalOperator: 'EQUAL',
				tag: {
					TagName: 'IOT_DEVICE',
					TagPropertyKey: 'TagProperty_DISTRICT',
					value: {
						valueType: 'VALUE_TYPE_STRING',
						stringValues: [],
					},
				},
			},
		},
		{
			baseCondition: {
				logicalOperator: 'EQUAL',
				tag: {
					TagName: 'IOT_DEVICE',
					TagPropertyKey: 'TagProperty_TEHSIL',
					value: {
						valueType: 'VALUE_TYPE_STRING',
						stringValues: [],
					},
				},
			},
		},
		{
			baseCondition: {
				logicalOperator: 'EQUAL',
				tag: {
					TagName: 'IOT_DEVICE',
					TagPropertyKey: 'LOCATION_AREA',
					value: {
						valueType: 'VALUE_TYPE_STRING',
						stringValues: [],
					},
				},
			},
		},
	],
};
const INIT_TIME_FILTERS = [
	{
		id: '24hrs',
		label: 'Last connected more than 24 hours ago',
		timeLimit: 86400,
		selected: false,
		icon: ExclamationIcon,
	},
	{
		id: '1week',
		label: 'Last connected more than 1 week ago',
		timeLimit: 604800,
		selected: false,
		icon: DoubleExclamationIcon,
	},
	{
		id: '1month',
		label: 'Last connected more than 1 month ago',
		timeLimit: 2592000,
		selected: false,
		icon: TripleExclamationIcon,
	},
];
const LeftPanel = ({ mapsRef }: { mapsRef: any }) => {
	const dispatch = useAppDispatch();
	const searchParams = useSearchParams();
	const { client } = useApolloClientAndMsalAuth();
	const [timeFilters, setTimeFilters] = useState(INIT_TIME_FILTERS);
	const activeAlertFilters = useAppSelector(
		(s) => s.telematics.activeAlertFilters,
	);
	const activeTagFilters = useAppSelector(
		(s) => s.telematics.activeTagFilters,
	);
	const liveMachinesData = useAppSelector(
		(s) => s.telematics.liveMachinesData,
	);
	const selectedLiveMachine = useAppSelector(
		(s) => s.telematics.selectedLiveMachine,
	);

	const [isPanelOpen, setIsPanelOpen] = useState(true);
	const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
	const [appliedTagFilters, setAppliedTagFilters] =
		useState<ITagFilters>(defaultTagFilters);
	const [stateListOptions, setStateListOptions] = useState<any>([]);
	const [districtListOptions, setDistrictLisOptions] = useState<any>([]);
	const [tehsilListOption, setTehsilListOption] = useState<any>([]);
	const [villageListOption, setVillageListOption] = useState<any>([]);

	useQuery(GET_ALL_FILTER_TAGS_V2, {
		client: client,
		variables: {
			input: {
				nodeType: 'NODE_COUNTRY',
				nodeName: 'India',
				depthChild: 1,
			},
		},
		onCompleted: (data) => {
			const response = data.getAllFilterTagsV2.nodes;
			if (response && response.length > 0) {
				const result = transformResponse(response[0].nodes);
				setStateListOptions(result);
			}
		},
	});

	const onStateChange = async (stateName: string) => {
		const response: any = await client.query({
			query: GET_ALL_FILTER_TAGS_V2,
			variables: {
				input: {
					nodeType: 'NODE_STATE',
					nodeName: stateName,
					depthChild: 1,
				},
			},
		});

		const unformattedResult =
			response.data.getAllFilterTagsV2.nodes[0].nodes;
		const result = transformResponse(unformattedResult);
		setDistrictLisOptions(result);
	};

	const onDistrictChange = async (districtName: string) => {
		const response: any = await client.query({
			query: GET_ALL_FILTER_TAGS_V2,
			variables: {
				input: {
					nodeType: 'NODE_DISTRICT',
					nodeName: districtName,
					depthChild: 1,
				},
			},
		});

		const unformattedResult =
			response.data.getAllFilterTagsV2.nodes[0].nodes;
		const result = transformResponse(unformattedResult);
		setTehsilListOption(result);
	};

	const onTehsilChange = async (tehsilName: string) => {
		const response: any = await client.query({
			query: GET_ALL_FILTER_TAGS_V2,
			variables: {
				input: {
					nodeType: 'NODE_TEHSIL',
					nodeName: tehsilName,
					depthChild: 1,
				},
			},
		});

		const unformattedResult =
			response.data.getAllFilterTagsV2.nodes[0].nodes;
		const result = transformResponse(unformattedResult);
		setVillageListOption(result);
	};

	const transformResponse = (nodes: any) => {
		const transformedArray = nodes.map((node: any) => ({
			label: node.data.name,
			value: node.data.name,
		}));
		return transformedArray;
	};

	const liveMachinesDataList = useMemo(
		() => Object.values(liveMachinesData),
		[liveMachinesData],
	);

	const alertsSummary = useMemo(() => {
		const result: Record<string, IAlertSummary> = {
			'TRIANGLE-RED': {
				count: 0,
				...ALERT_ICONS['TRIANGLE-RED'],
			},
			'SQUARE-RED': {
				count: 0,
				...ALERT_ICONS['SQUARE-RED'],
			},
			'ARROW-RED': {
				count: 0,
				...ALERT_ICONS['ARROW-RED'],
			},
			'CIRCLE-RED': {
				count: 0,
				...ALERT_ICONS['CIRCLE-RED'],
			},
			'ARROW-YELLOW': {
				count: 0,
				...ALERT_ICONS['ARROW-YELLOW'],
			},
			'CIRCLE-YELLOW': {
				count: 0,
				...ALERT_ICONS['CIRCLE-YELLOW'],
			},
			'CIRCLE-PINK': {
				count: 0,
				...ALERT_ICONS['CIRCLE-PINK'],
			},
			'ARROW-BLUE': {
				count: 0,
				...ALERT_ICONS['ARROW-BLUE'],
			},
			'SQUARE-BLUE': {
				count: 0,
				...ALERT_ICONS['SQUARE-BLUE'],
			},
			'ARROW-GREEN': {
				count: 0,
				...ALERT_ICONS['ARROW-GREEN'],
			},
			'SQUARE-GREEN': {
				count: 0,
				...ALERT_ICONS['SQUARE-GREEN'],
			},
		};
		liveMachinesDataList.forEach((el: ILiveMachineData) => {
			if (result[el.machineIcon.iconType] !== undefined) {
				result[el.machineIcon.iconType].count += 1;
			}
		});
		return Object.values(result);
	}, [liveMachinesDataList]);
	const filteredMachinesList = useMemo(() => {
		const filter1List = liveMachinesDataList.filter((el) => {
			return (
				activeAlertFilters.size === 0 ||
				activeAlertFilters.has(el.machineIcon.iconType)
			);
		});
		const finalList = filter1List.filter((el) => {
			const lastConnectedTime = el.timeRange.startTime.seconds;
			let timeCondition = true;
			const selectedTimeFilter = timeFilters.find((el) => el.selected);
			if (selectedTimeFilter) {
				const timeRange = selectedTimeFilter.timeLimit;
				if (lastConnectedTime + timeRange > Date.now() / 1000)
					timeCondition = false;
			}
			return timeCondition;
		});
		return finalList;
	}, [liveMachinesDataList, activeAlertFilters, timeFilters]);

	const selectMachine = ({
		placeId,
		selectedLayer,
		coordinates: { lat, lng },
	}: {
		placeId: string;
		selectedLayer: Layer;
		coordinates: Coordinates;
	}) => {
		const key = `${placeId}-${selectedLayer.PlaceNamespace}-${selectedLayer.PlaceSourceType}-${selectedLayer.PlaceVisibleType}`;
		const zoom = mapsRef.current.getZoom();
		dispatch(setSelectedLiveMachine(key));
		searchParams.set('machineId', liveMachinesData[key].machineId);
		searchParams.set('lat', `${lat}`);
		searchParams.set('lng', `${lng}`);
		if (zoom < 17) {
			mapsRef.current.setZoom(17);
		}
		smoothlyPanMapsTo({
			mapInstance: mapsRef.current,
			mapOptions,
			lat,
			lng,
			zoom: zoom < 17 ? 17 : zoom,
		});
		const newPath = `telematics?${searchParams.toString()}`;
		dispatch(redirect(newPath));
		// history.push({ search: searchParams.toString() });
	};

	const isSignalStrengthLow = ({ tags }: { tags: PlaceTag[] }): boolean => {
		let isSignalStrengthLowFlag = false;
		for (let i = 0; i < tags.length; i++) {
			let { TagPropertyKey: tagKey, value: tagValue }: PlaceTag = tags[i];
			let tempTagValues = extractValuesForPlaceTag(tagValue);
			if (
				tagKey === 'NETWORK_STATUS' &&
				tempTagValues.length > 0 &&
				tempTagValues.includes('LOW_SIGNAL_STRENGTH')
			) {
				isSignalStrengthLowFlag = true;
			}
		}
		return isSignalStrengthLowFlag;
	};

	const isBatteryLow = ({ tags }: { tags: PlaceTag[] }): boolean => {
		let internalBatteryLowFlag = false;
		for (let i = 0; i < tags.length; i++) {
			let { TagPropertyKey: tagKey, value: tagValue }: PlaceTag = tags[i];
			let tempTagValues = extractValuesForPlaceTag(tagValue);
			if (
				tagKey === 'INTERNAL_BATTERY_STATUS' &&
				tempTagValues.length > 0 &&
				tempTagValues.includes('LOW_BATTERY')
			) {
				internalBatteryLowFlag = true;
			}
		}
		return internalBatteryLowFlag;
	};

	const getLastConnected = ({
		placeId,
		selectedLayer,
	}: {
		placeId: string;
		selectedLayer: Layer;
	}) => {
		let key = `${placeId}-${selectedLayer.PlaceNamespace}-${selectedLayer.PlaceSourceType}-${selectedLayer.PlaceVisibleType}`;
		const placeTags = liveMachinesData[key]?.placeTags;
		let lastConnectedTime = Date.now() / 1000;
		placeTags.some((el: PlaceTag) => {
			if (el.TagName === 'IOT_DEVICE') {
				if (el.TagPropertyKey === 'LAST_CONNECTED') {
					lastConnectedTime -= extractValuesForPlaceTag(el.value)[0];
					return true;
				}
			}
			return false;
		});
		return getDuration(lastConnectedTime);
	};
	const handleTimeLimit = (currentId: string) => {
		setTimeFilters((prev) => {
			return prev.map((entity) => {
				if (entity.id === currentId)
					return { ...entity, selected: !entity.selected };
				return { ...entity, selected: false };
			});
		});
	};

	const toggleAlertFilters = (iconType: MarkerIconType) => {
		let currentFilters: Set<MarkerIconType> = new Set(activeAlertFilters);
		if (currentFilters.has(iconType)) {
			currentFilters.delete(iconType);
		} else {
			currentFilters.add(iconType);
		}
		dispatch(setActiveAlertFilters(currentFilters));
	};

	const toggleFilterModal = (isOpen: boolean) => {
		setIsFilterModalVisible(isOpen);
	};

	const updateTagFilters = useCallback(
		(propertyKey: string, value: string | null) => {
			let tempTagFilters = JSON.parse(JSON.stringify(appliedTagFilters));
			const el = {
				baseCondition: {
					logicalOperator: 'EQUAL',
					tag: {
						TagName: 'IOT_DEVICE',
						TagPropertyKey: propertyKey,
						value: {
							valueType: 'VALUE_TYPE_STRING',
							stringValues: [value],
						},
					},
				},
			};

			if (propertyKey === 'TagProperty_STATE') {
				setDistrictLisOptions([]);
				setTehsilListOption([]);
				setVillageListOption([]);
				if (value === null) {
					tempTagFilters.conditions[0] = null;
				} else {
					onStateChange(value);
					tempTagFilters.conditions[0] = el;
				}
				tempTagFilters.conditions[1] = null;
				tempTagFilters.conditions[2] = null;
				tempTagFilters.conditions[3] = null;
			} else if (propertyKey === 'TagProperty_DISTRICT') {
				setTehsilListOption([]);
				setVillageListOption([]);
				if (value === null) {
					tempTagFilters.conditions[1] = null;
				} else {
					onDistrictChange(value);
					tempTagFilters.conditions[1] = el;
				}
				tempTagFilters.conditions[2] = null;
				tempTagFilters.conditions[3] = null;
			} else if (propertyKey === 'TagProperty_TEHSIL') {
				setVillageListOption([]);
				if (value === null) {
					setVillageListOption([]);
					tempTagFilters.conditions[2] = null;
				} else {
					onTehsilChange(value);
					tempTagFilters.conditions[2] = el;
				}
				tempTagFilters.conditions[3] = null;
			} else if (propertyKey === 'LOCATION_AREA') {
				if (value === null) {
					tempTagFilters.conditions[3] = null;
				} else {
					tempTagFilters.conditions[3] = el;
				}
			}
			setAppliedTagFilters(() => tempTagFilters);
		},
		[appliedTagFilters],
	);

	const applyTagFilters = useCallback(() => {
		const payloadConditions = appliedTagFilters.conditions.filter(
			(el) =>
				el !== null &&
				el?.baseCondition?.tag?.value?.stringValues?.length !== 0,
		);

		dispatch(
			setActiveTagFilters({
				...appliedTagFilters,
				conditions: payloadConditions,
			}),
		);
	}, [appliedTagFilters, dispatch]);

	const clearTagFilter = useCallback(() => {
		setAppliedTagFilters(defaultTagFilters);
		dispatch(setActiveTagFilters({}));
	}, [dispatch]);

	return (
		<div className={styles.collapseContainer}>
			<ExpandMore
				expand={isPanelOpen}
				onClick={() => setIsPanelOpen(!isPanelOpen)}
				aria-expanded={isPanelOpen}
				aria-label="show more"
				sx={{
					position: 'absolute',
					right: isPanelOpen ? '-20px' : '-25px',
					top: '20px',
					border: '1px solid',
					backgroundColor: isPanelOpen ? '#a3a3a3' : '#1876d2',
					'&:hover': {
						backgroundColor: '#a3a3a3',
					},
				}}
			>
				<ExpandMoreIcon />
			</ExpandMore>
			<Collapse
				orientation="horizontal"
				in={isPanelOpen}
				sx={{
					height: '100%',
					width: 'auto',
					transitionDuration: '323ms',
				}}
				// collapsedSize={40}
				timeout="auto"
				// unmountOnExit
			>
				<div className={styles.container}>
					<div className={styles.header}>
						<div className={styles.title}>
							<span>
								Total Assets: {liveMachinesDataList.length}
							</span>
							<span>
								<IconButton
									sx={{
										borderRadius: '0%',
										padding: '4px',
										// border: '1px solid',
									}}
									onClick={() => toggleFilterModal(true)}
								>
									<Badge
										badgeContent={
											activeTagFilters?.conditions
												?.length ?? 0
										}
										color="error"
										showZero
									>
										<FilterAltIcon color="disabled" />
									</Badge>
								</IconButton>
							</span>
						</div>
						<div className={styles.alertsSummary}>
							{timeFilters.map((el) => {
								return (
									<Box
										key={el.id}
										onClick={() => {
											handleTimeLimit(el.id);
										}}
										title={el.label}
										sx={{
											cursor: 'pointer',
											height: '40px',
											width: '40px',
											marginTop: '5px',
											backgroundColor: el.selected
												? 'grey'
												: 'unset',
											borderRadius: '10px',
										}}
									>
										<el.icon
											color={
												el.selected
													? 'secondary'
													: 'primary'
											}
										/>
									</Box>
								);
							})}
							{alertsSummary.map((el) => (
								<div
									key={el.iconType}
									className={cx(styles.alert, {
										[styles.selected]:
											activeAlertFilters.has(el.iconType),
									})}
									onClick={() =>
										toggleAlertFilters(el.iconType)
									}
									role="button"
								>
									<div className={styles.icon}>
										<img
											src={el.url}
											alt={el.desc}
											title={el.desc}
										/>
									</div>
									<div className={styles.count}>
										{el.count}
									</div>
								</div>
							))}
						</div>
					</div>
					<div className={styles.content}>
						{filteredMachinesList.map((el: ILiveMachineData) => (
							<div
								className={cx(styles.card, {
									[styles.selected]:
										el.placeId ===
										liveMachinesData[selectedLiveMachine!]
											?.placeId,
								})}
								key={`${el.placeId}-${el.machineId}`}
								role="button"
								onClick={() =>
									selectMachine({
										placeId: el.placeId,
										selectedLayer: el.selectedLayer,
										coordinates:
											el.placeGeometryResult.geometry,
									})
								}
							>
								<div className={styles.heading}>
									<div className={styles.title}>
										<div className={styles.icon}>
											<img
												src={el.machineIcon.url}
												alt={el.machineId}
												title={
													el.machineIcon.desc ?? ''
												}
											/>
										</div>
										<div className={styles.label}>
											{el.machineId}
										</div>
									</div>
									{isSignalStrengthLow({
										tags: el.placeTags,
									}) ? (
										<div
											className={styles.lowSignalStrength}
										>
											<img
												src={LowSignalStrength}
												alt="low-cellular-network"
											/>
										</div>
									) : null}
									{isBatteryLow({
										tags: el.placeTags,
									}) ? (
										<div
											className={styles.lowBatteryStatus}
										>
											<img
												src={LowBattery}
												alt="low-battery"
											/>
										</div>
									) : null}
								</div>
								<div
									className={styles.description}
								>{`Last connected: ${getLastConnected({
									placeId: el.placeId,
									selectedLayer: el.selectedLayer,
								})} ago`}</div>
							</div>
						))}
						<div className={styles.placeholderCard}>
							No more data
						</div>
					</div>
				</div>
			</Collapse>
			<Backdrop
				sx={{
					color: '#fff',
					zIndex: (theme) => theme.zIndex.drawer + 1,
				}}
				open={isFilterModalVisible}
			>
				<Modal
					open={isFilterModalVisible}
					hideBackdrop
					keepMounted
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: '30%',
						background: '#FFFFFF',
						backgroundColor: '#FFFFFF',
						borderRadius: '8px',
						paddingX: '10px',
						paddingY: '32px',
					}}
					onClose={() => toggleFilterModal(false)}
				>
					<>
						<IconButton
							sx={{
								borderRadius: '50%',
								padding: '5px',
								position: 'absolute',
								right: '5px',
								top: '5px',
								// border: '1px solid',
							}}
							onClick={() => toggleFilterModal(false)}
						>
							<HighlightOffIcon color="error" />
						</IconButton>
						<Filters
							applyTagFilters={applyTagFilters}
							clearTagFilters={clearTagFilter}
							stateListOptions={stateListOptions}
							districtListOptions={districtListOptions}
							tehsilListOption={tehsilListOption}
							villageListOption={villageListOption}
							updateTagFilters={updateTagFilters}
						/>
					</>
				</Modal>
			</Backdrop>
		</div>
	);
};

export default React.memo(LeftPanel);

interface ExpandMoreProps extends IconButtonProps {
	expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
	const { expand, ...other } = props;
	return <IconButton {...other} />;
})(({ theme, expand }) => ({
	transform: !expand ? 'rotate(270deg)' : 'rotate(90deg)',
	marginLeft: 'auto',
	transition: theme.transitions.create('transform', {
		duration: theme.transitions.duration.shortest,
	}),
}));
